import { effectiveLoadG } from './load.js';
import { workingSets } from './sets.js';
import { estimateOneRepMaxG } from './one-rep-max.js';
import type { LoggedSet, PerformedExercise } from './types.js';

/**
 * Previous-performance selection.
 *
 * Pure by design. The repository fetches candidate history; this module only
 * decides which of it to show. That separation is what lets the iPhone run the
 * identical logic against its offline cache — the phone must be able to
 * pre-fill "Previous: 80 kg × 10" with the PC switched off.
 *
 * Selection is **across all sessions**, not restricted to the current plan
 * (requirement H3): the last time you actually benched is the useful reference,
 * whichever plan it happened under.
 */

export interface PreviousSet {
  setIndex: number;
  loadG: number | null;
  reps: number | null;
  durationS: number | null;
  rpe: number | null;
}

export interface PreviousPerformance {
  sessionId: string;
  sessionExerciseId: string;
  exerciseId: string;
  performedAt: number;
  sets: PreviousSet[];
  /** Heaviest completed working set, the natural headline value. */
  topSet: PreviousSet | null;
  /** Best estimated 1RM in that session, when computable. */
  bestEstimatedOneRepMaxG: number | null;
}

export interface SelectPreviousOptions {
  /** Candidate history for one exercise. Order does not matter. */
  history: readonly PerformedExercise[];
  /** Exclude the workout in progress so it cannot match itself. */
  excludeSessionId?: string;
  /** Ignore anything at or after this instant. */
  beforeEpochMs?: number;
  oneRepMaxFormulaId?: string;
}

function toPreviousSet(set: LoggedSet): PreviousSet {
  const load = effectiveLoadG(set);
  return {
    setIndex: set.setIndex,
    loadG: load.known ? load.value : null,
    reps: set.reps,
    durationS: set.durationS,
    rpe: set.rpe,
  };
}

/**
 * The most recent qualifying performance, or null.
 *
 * A session only qualifies if it has at least one **completed working set** —
 * a workout where the exercise was started and abandoned is not a useful
 * reference for what to load next time.
 */
export function selectPreviousPerformance(
  options: SelectPreviousOptions,
): PreviousPerformance | null {
  const { history, excludeSessionId, beforeEpochMs, oneRepMaxFormulaId } = options;

  const candidates = history
    .filter((entry) => entry.sessionId !== excludeSessionId)
    .filter((entry) => beforeEpochMs === undefined || entry.sessionStartedAt < beforeEpochMs)
    .filter((entry) => workingSets(entry.sets).some((s) => s.isCompleted))
    // Most recent first. Ties broken by id so the result is deterministic
    // rather than dependent on input ordering.
    .sort((a, b) =>
      b.sessionStartedAt !== a.sessionStartedAt
        ? b.sessionStartedAt - a.sessionStartedAt
        : b.sessionId.localeCompare(a.sessionId),
    );

  const chosen = candidates[0];
  if (chosen === undefined) return null;

  const completed = workingSets(chosen.sets).filter((s) => s.isCompleted);
  const sets = completed.map(toPreviousSet);

  let topSet: PreviousSet | null = null;
  let bestOneRepMax: number | null = null;

  for (const set of completed) {
    const load = effectiveLoadG(set);
    if (!load.known) continue;

    const asPrevious = toPreviousSet(set);
    if (topSet === null || (asPrevious.loadG ?? 0) > (topSet.loadG ?? 0)) {
      topSet = asPrevious;
    }

    if (set.reps !== null && set.reps > 0) {
      const estimate = estimateOneRepMaxG(load.value, set.reps, oneRepMaxFormulaId);
      if (estimate !== null && (bestOneRepMax === null || estimate > bestOneRepMax)) {
        bestOneRepMax = estimate;
      }
    }
  }

  return {
    sessionId: chosen.sessionId,
    sessionExerciseId: chosen.sessionExerciseId,
    exerciseId: chosen.exerciseId,
    performedAt: chosen.sessionStartedAt,
    sets,
    topSet,
    bestEstimatedOneRepMaxG: bestOneRepMax,
  };
}

/**
 * Pre-fill values for a set about to be performed.
 *
 * Matches the corresponding set index where possible, and otherwise falls back
 * to the last set of the previous session — the behaviour that makes "usually
 * just tap ✓" work (requirement B2).
 */
export function prefillForSet(
  previous: PreviousPerformance | null,
  setIndex: number,
): { loadG: number | null; reps: number | null; durationS: number | null; source: 'matched_set' | 'last_set' | 'none' } {
  if (previous === null || previous.sets.length === 0) {
    return { loadG: null, reps: null, durationS: null, source: 'none' };
  }

  const matched = previous.sets.find((s) => s.setIndex === setIndex);
  if (matched !== undefined) {
    return {
      loadG: matched.loadG,
      reps: matched.reps,
      durationS: matched.durationS,
      source: 'matched_set',
    };
  }

  const last = previous.sets[previous.sets.length - 1];
  if (last === undefined) {
    return { loadG: null, reps: null, durationS: null, source: 'none' };
  }
  return { loadG: last.loadG, reps: last.reps, durationS: last.durationS, source: 'last_set' };
}

export interface LoadRange {
  minG: number;
  maxG: number;
}

/**
 * The lowest and highest qualifying load across a set of previous sets, for
 * the compact "Previous: 30 kg" / "Previous: 25–35 kg" display.
 *
 * Pure summarisation only — which sets are "qualifying" is already decided
 * upstream by `selectPreviousPerformance()` (completed working sets only);
 * this only ignores a `null` load (no weight recorded, or not applicable,
 * e.g. a bodyweight set) among whatever sets it is handed. Order-independent
 * by construction (`Math.min`/`Math.max` over the whole list), and returns
 * `null` rather than a degenerate `{0, 0}` when nothing is loadable.
 */
export function summariseLoadRange(sets: readonly { loadG: number | null }[]): LoadRange | null {
  const loads = sets.map((set) => set.loadG).filter((loadG): loadG is number => loadG !== null);
  if (loads.length === 0) return null;
  return { minG: Math.min(...loads), maxG: Math.max(...loads) };
}
